import React, { useEffect, useState, useCallback } from "react";
import { Modal, Tooltip, message, Card, Button } from "antd";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { v1 as uuid } from "uuid";
import { fabric } from "fabric";
import templatesIcon from "../../assets/Templates/template-icon.png";
import "./styles.css";
import { boardServices } from "../../api";
import { useCanvas } from "../../context/canvasContext";
import { useDocument } from "../../context/documentInfoContex";
import TemplatePreview from "./TemplatePreview";
import { AppstoreOutlined } from "@ant-design/icons";

const Templates = () => {
    const { canvasRef } = useCanvas();
    const { setSavingData } = useDocument();
    const { id } = useParams();
    const [templates, setTemplates] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [isPreviewVisible, setIsPreviewVisible] = useState(false);

    // Fetch templates
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const res = await boardServices.getAllTemplates();
                if (Array.isArray(res?.data) && res.data.length > 0) {
                    setTemplates(res.data.map(template => template.templateData));
                }
            } catch (error) {
                console.error("Error fetching templates:", error);
            }
        };

        fetchTemplates();
    }, [id]);

    // Create template
    const handleCreateTemplate = async () => {
        if (!canvasRef.current) return message.error("Canvas is not initialized.");

        try {
            const canvas = canvasRef.current;
            const canvasData = canvas.toJSON(["id", "selectable", "type"]);
            const templateImage = canvas.toDataURL("image/png");

            await boardServices.createTemplate({
                templateData: [{ id: `ideation-template-${uuid()}`, data: canvasData, img: templateImage }],
            });

            message.success("Template created successfully!");
        } catch (error) {
            console.error("Error:", error);
            message.error("Failed to create template.");
        }
    };


    const handleTemplateClick = useCallback((template) => {
        Modal.confirm({
            title: "Apply or Preview Template?",
            content: "Do you want to apply this template to the canvas or preview it?",
            okText: "Apply",
            cancelText: "Preview",
            onOk: () => applyTemplate(template),
            onCancel: () => {
                setPreviewTemplate(template);
                setIsPreviewVisible(true);
            }
        });
    }, [canvasRef, id]);

    const applyTemplate = async (template) => {
        if (!canvasRef.current) return console.error("Canvas is not initialized.");

        const canvas = canvasRef.current;
        const templateObjects = template?.data?.objects;
        if (!templateObjects) return message.error("Invalid template data.");

        try {
            await new Promise((resolve) => {
                fabric.util.enlivenObjects(templateObjects, (objects) => {
                    const canvasWidth = canvas.width;
                    const canvasHeight = canvas.height;

                    // Get template dimensions
                    const templateWidth = template.data.width || canvasWidth;
                    const templateHeight = template.data.height || canvasHeight;

                    // Calculate scale factor
                    const scaleX = canvasWidth / templateWidth;
                    const scaleY = canvasHeight / templateHeight;
                    const scale = Math.min(scaleX, scaleY); // Maintain aspect ratio

                    objects.forEach((obj) => {
                        obj.set({
                            left: obj.left * scale,
                            top: obj.top * scale,
                            scaleX: obj.scaleX * scale,
                            scaleY: obj.scaleY * scale,
                        });
                        canvas.add(obj);
                    });

                    canvas.renderAll();
                    resolve();
                });
            });

            const canvasData = canvas.toJSON(["id", "selectable", "type"]);
            await boardServices.updateBoardData(id, canvasData, setSavingData);
            message.success("Template applied successfully!");
        } catch (error) {
            console.error("Error applying template:", error);
            message.error("Failed to apply template.");
        }

        setIsModalVisible(false);
    };



    const filteredTemplates = templates
        .map(t => t[0])
        .filter(template => template?.id?.includes("ideation") && template?.img);

    return (
        <div>
            {/* Template Icon */}
            <Tooltip title="Templates" placement="right">
                <Button
                    type="ghost"
                    shape="circle"
                    style={{ marginBottom: "20px" }}
                    icon={<AppstoreOutlined />}
                    onClick={() => setIsModalVisible(true)}
                />
            </Tooltip>

            {/* Modal for Templates */}
            <Modal
                title="Choose a Template"
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                width={700}
            >
                <div className="template-grid">
                    {filteredTemplates.length > 0 ? (
                        filteredTemplates.map((template, index) => (
                            <Card
                                className="template-card"
                                key={template.id}
                                hoverable
                                cover={<img src={template.img} alt={`Template ${index + 1}`} />}
                                onClick={() => handleTemplateClick(template)}
                            >
                                <Card.Meta title={`Template ${index + 1}`} />
                            </Card>
                        ))
                    ) : (
                        <p>No templates available</p>
                    )}
                </div>
            </Modal>

            {/* Create Template Button */}
            {/* <button onClick={handleCreateTemplate}>Create Template</button> */}

            {/* Template Preview Modal */}
            {isPreviewVisible && (
                <div style={{ position: "relative", zIndex: 1000 }}>
                    <TemplatePreview
                        visible={isPreviewVisible}
                        onClose={() => setIsPreviewVisible(false)}
                        template={previewTemplate}
                        applyTemplate ={applyTemplate}
                    />
                </div>
            )}

        </div>
    );
};

export default Templates;
